import { useState, useEffect } from 'react';
import { Shield, User, CreditCard, Lock, LogOut, Trash2 } from 'lucide-react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const featureLabels = {
  oneview: 'Strata Record Synthesis',
  observer_triangulation: 'Observer Triangulation',
  narrative_prompting: 'Narrative Prompting',
  role_fit: 'Role Fit Analysis',
  succession_readiness: 'Succession Readiness',
  proof: 'Proof Document',
  proof_bundle: 'Proof Bundle',
  re_synthesis: 'Re-Synthesis',
  expert_review: 'Expert Review',
  annual_review: 'Annual Review'
};

export default function Record() {
  const { user, logout } = useAuth();
  const [purchases, setPurchases] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [purchasesRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/purchases`, { credentials: 'include' }),
        fetch(`${API_URL}/api/dashboard/stats`, { credentials: 'include' })
      ]);

      if (purchasesRes.ok) setPurchases(await purchasesRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  const handleDeleteAccount = async () => {
    if (!confirm('Are you sure you want to delete your account? This action cannot be undone and all your data will be permanently deleted.')) {
      return;
    }
    
    toast.info('Account deletion would be processed here.');
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <div className="w-12 h-12 border-4 border-amber-600/30 border-t-amber-600 rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 max-w-4xl" data-testid="record-page">
        {/* Header */}
        <div>
          <h1 className="font-display text-3xl text-white mb-2">Record</h1>
          <p className="text-slate-400">
            Manage your account, view purchases, and control your data.
          </p>
        </div>

        {/* Profile Card */}
        <Card className="strata-card">
          <CardHeader>
            <CardTitle className="font-display text-xl text-white flex items-center gap-2">
              <User className="w-5 h-5 text-amber-500" />
              Profile
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              <Avatar className="h-20 w-20 border-2 border-slate-mid">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="bg-amber-600 text-white text-2xl">
                  {user?.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h3 className="text-xl text-white font-medium">{user?.name}</h3>
                <p className="text-slate-400">{user?.email}</p>
                {user?.current_role && (
                  <p className="text-slate-500 text-sm mt-1">{user.current_role}</p>
                )}
              </div>
            </div>

            {(user?.industry || user?.region) && (
              <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-slate-mid/50">
                {user?.industry && (
                  <div>
                    <p className="text-slate-500 text-sm">Industry</p>
                    <p className="text-white">{user.industry}</p>
                  </div>
                )}
                {user?.region && (
                  <div>
                    <p className="text-slate-500 text-sm">Region</p>
                    <p className="text-white">{user.region}</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Strata Record Stats */}
        <Card className="strata-card">
          <CardHeader>
            <CardTitle className="font-display text-xl text-white flex items-center gap-2">
              <Shield className="w-5 h-5 text-state-integrate" />
              Your Strata Record
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <p className="font-mono text-3xl text-white">{stats?.sources_count || 0}</p>
                <p className="text-slate-400 text-sm">Sources</p>
              </div>
              <div className="text-center">
                <p className="font-mono text-3xl text-white">{stats?.processed_count || 0}</p>
                <p className="text-slate-400 text-sm">Processed</p>
              </div>
              <div className="text-center">
                <p className="font-mono text-3xl text-white">{stats?.narratives_count || 0}</p>
                <p className="text-slate-400 text-sm">Narratives</p>
              </div>
              <div className="text-center">
                <p className="font-mono text-3xl text-white">{purchases.length}</p>
                <p className="text-slate-400 text-sm">Purchases</p>
              </div>
            </div>

            <Separator className="my-6 bg-slate-mid/50" />

            <div className="flex flex-wrap gap-3">
              {stats?.has_synthesis && (
                <span className="px-3 py-1 bg-green-500/10 text-green-400 rounded-full text-sm">
                  Strata Record Synthesis Complete
                </span>
              )}
              {stats?.has_rolefit && (
                <span className="px-3 py-1 bg-green-500/10 text-green-400 rounded-full text-sm">
                  Role Fit Complete
                </span>
              )}
              {stats?.has_proof && (
                <span className="px-3 py-1 bg-green-500/10 text-green-400 rounded-full text-sm">
                  Proof Document
                </span>
              )}
              {!stats?.has_synthesis && !stats?.has_rolefit && !stats?.has_proof && (
                <span className="text-slate-500 text-sm">No features unlocked yet</span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Purchase History */}
        <Card className="strata-card">
          <CardHeader>
            <CardTitle className="font-display text-xl text-white flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-amber-500" />
              Purchase History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {purchases.length > 0 ? (
              <div className="space-y-3">
                {purchases.map((purchase, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center justify-between p-4 rounded-lg bg-slate-mid/30 border border-slate-mid/50"
                  >
                    <div>
                      <p className="text-white font-medium">
                        {featureLabels[purchase.feature] || purchase.feature}
                      </p>
                      <p className="text-slate-500 text-sm font-mono">
                        {new Date(purchase.purchased_at).toLocaleDateString()}
                      </p>
                    </div>
                    <span className="text-green-400 text-sm">Active</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-400 text-center py-8">No purchases yet</p>
            )}
          </CardContent>
        </Card>

        {/* Privacy & Data */}
        <Card className="strata-card">
          <CardHeader>
            <CardTitle className="font-display text-xl text-white flex items-center gap-2">
              <Lock className="w-5 h-5 text-state-ground" />
              Privacy & Data
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 rounded-lg bg-slate-mid/30 border border-slate-mid/50">
              <p className="text-white font-medium mb-2">Your Data, Your Control</p>
              <p className="text-slate-400 text-sm leading-relaxed">
                Your Strata Record belongs to you. We store it, but we do not own it. 
                You can export, share, or delete your data at any time.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button variant="outline" className="strata-button-secondary flex-1">
                Export All Data
              </Button>
              <Button 
                variant="outline" 
                className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10"
                onClick={handleDeleteAccount}
                data-testid="delete-account-btn"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Account
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Sign Out */}
        <Card className="strata-card">
          <CardContent className="py-6">
            <Button 
              onClick={handleLogout}
              variant="outline"
              className="w-full strata-button-secondary"
              data-testid="signout-btn"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sign Out
            </Button>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
